import { useCallback, useEffect } from 'react';
import { Container } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';

import Footer from '../../molecule/footer/footer';
import HeroSection from '../../molecule/hero/hero';
import ProductShowcase from '../../molecule/product/product';
import Collection from '../../molecule/collection/collection';
import Founder from '../../molecule/founder/founder';
import { Shimmer } from '../../atome/motion/motion.styled';
import { RootState } from '../../../redux';
import { productsFailed, productsLoading, setProducts } from '../../../redux/product';
import { ProductServices } from '../../../api/product';
import {
  ErrorPanel,
  RetryButton,
  SkeletonCard,
  SkeletonGrid,
  StateSection,
} from './home.styled';

const CatalogueSkeleton = () => (
  <StateSection>
    <Container maxWidth="lg">
      <SkeletonGrid>
        {[0, 1, 2].map((i) => (
          <SkeletonCard key={i}>
            <Shimmer $height="250px" $radius="0" />
            <Shimmer $height="12px" $radius="6px" />
            <Shimmer $height="22px" />
            <Shimmer $height="14px" />
          </SkeletonCard>
        ))}
      </SkeletonGrid>
    </Container>
  </StateSection>
);

const Home = () => {
  const dispatch = useDispatch();
  const { items, status, error } = useSelector((state: RootState) => state.product);

  const loadProducts = useCallback(async () => {
    dispatch(productsLoading());
    try {
      dispatch(setProducts(await ProductServices.getProducts()));
    } catch (err: any) {
      dispatch(productsFailed(err?.message || 'Failed to load products'));
    }
  }, [dispatch]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const isFirstLoad = status === 'loading' && items.length === 0;
  const failedWithNothing = status === 'error' && items.length === 0;

  const renderCatalogue = () => {
    if (isFirstLoad) return <CatalogueSkeleton />;

    if (failedWithNothing) {
      return (
        <StateSection>
          <Container maxWidth="lg">
            <ErrorPanel>
              <h3>We could not reach the shop</h3>
              <p>{error || 'Something went wrong while loading our products.'}</p>
              <RetryButton onClick={loadProducts}>Try again</RetryButton>
            </ErrorPanel>
          </Container>
        </StateSection>
      );
    }

    if (items.length === 0) {
      return (
        <StateSection>
          <Container maxWidth="lg">
            <ErrorPanel>
              <h3>The shelf is being restocked</h3>
              <p>New formulas are on their way. Check back very soon.</p>
            </ErrorPanel>
          </Container>
        </StateSection>
      );
    }

    // One product -> spotlight it. More than one -> editorial catalogue grid.
    return items.length === 1 ? (
      <ProductShowcase product={items[0]} variant="solo" />
    ) : (
      <Collection products={items} />
    );
  };

  return (
    <>
      <HeroSection productCount={items.length} />
      {renderCatalogue()}
      <Founder />
      <Footer />
    </>
  );
};

export default Home;
